import {
  Braces,
  ChevronDown,
  ChevronUp,
  FileText,
  Heading1,
  Image as ImageIcon,
  Info,
  Minus,
  MousePointerClick,
  Plus,
  Table2,
  Trash2,
  Video,
  Vote as VoteIcon,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import type { MessageSection } from "../../../shared/types";
import { IconButton, newId } from "../../components/primitives";
import { TemplateTextarea } from "./ActionsEditor";
import { SlackField } from "./RichTextEditor";

function compactSummary(value: string) {
  return value
    .replace(/[<>*_~`#]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 72);
}

function sectionDescription(section: MessageSection) {
  switch (section.type) {
    case "text":
      return { label: "Text", summary: compactSummary(section.mrkdwn), icon: <FileText size={15} /> };
    case "heading":
      return { label: "Heading", summary: compactSummary(section.text), icon: <Heading1 size={15} /> };
    case "context":
      return { label: "Context", summary: compactSummary(section.text), icon: <Info size={15} /> };
    case "table":
      return {
        label: "Table",
        summary: `${section.rows.length} × ${section.rows[0]?.cells.length ?? 0}`,
        icon: <Table2 size={15} />,
      };
    case "actions":
      return {
        label: "Buttons",
        summary: `${section.actions.length} button${section.actions.length === 1 ? "" : "s"}`,
        icon: <MousePointerClick size={15} />,
      };
    case "vote":
      return { label: "Vote", summary: compactSummary(section.question), icon: <VoteIcon size={15} /> };
    case "image":
      return { label: "Image", summary: section.name, icon: <ImageIcon size={15} /> };
    case "video":
      return { label: "Video", summary: section.name, icon: <Video size={15} /> };
    case "file":
      return { label: "File", summary: section.name, icon: <FileText size={15} /> };
    case "markdown":
      return { label: "Markdown", summary: compactSummary(section.text), icon: <Braces size={15} /> };
    case "divider":
      return { label: "Divider", summary: "", icon: <Minus size={15} /> };
  }
}

export function SectionAccordionHeader({
  section,
  open,
  onToggle,
}: {
  section: MessageSection;
  open: boolean;
  onToggle: () => void;
}) {
  const description = sectionDescription(section);
  return (
    <button
      type="button"
      className="section-accordion-toggle"
      aria-expanded={open}
      aria-controls={`section-content-${section.id}`}
      onClick={onToggle}
    >
      <span className="section-accordion-icon">{description.icon}</span>
      <strong>{description.label}</strong>
      <span>{description.summary}</span>
      <ChevronDown className="section-accordion-chevron" size={15} />
    </button>
  );
}

export function SectionControls({
  index,
  count,
  canDelete,
  canMoveUp = index > 0,
  canMoveDown = index < count - 1,
  dragHandle,
  onMove,
  onDelete,
}: {
  index: number;
  count: number;
  canDelete: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  dragHandle: ReactNode;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="section-controls">
      {dragHandle}
      <IconButton label="Move up" disabled={!canMoveUp} onClick={() => onMove(-1)}>
        <ChevronUp size={15} />
      </IconButton>
      <IconButton label="Move down" disabled={!canMoveDown} onClick={() => onMove(1)}>
        <ChevronDown size={15} />
      </IconButton>
      <IconButton label="Remove" disabled={!canDelete} onClick={onDelete}>
        <Trash2 size={15} />
      </IconButton>
    </div>
  );
}

export function HeadingEditor({
  section,
  onChange,
}: {
  section: Extract<MessageSection, { type: "heading" }>;
  onChange: (section: MessageSection) => void;
}) {
  return (
    <div className="heading-editor">
      <Heading1 size={17} />
      <select
        value={section.level}
        aria-label="Heading level"
        onChange={(event) => onChange({ ...section, level: Number(event.target.value) })}
      >
        <option value={1}>H1</option>
        <option value={2}>H2</option>
        <option value={3}>H3</option>
        <option value={4}>H4</option>
      </select>
      <SlackField
        mode="plain"
        value={section.text}
        maxLength={150}
        ariaLabel="Heading text"
        placeholder="Announcement heading"
        onChange={(text) => onChange({ ...section, text })}
      />
    </div>
  );
}

export function ContextEditor({
  section,
  onChange,
}: {
  section: Extract<MessageSection, { type: "context" }>;
  onChange: (section: MessageSection) => void;
}) {
  return (
    <div className="context-editor">
      <Info size={15} />
      <span className="visually-hidden">Context text</span>
      <SlackField
        mode="mrkdwn"
        value={section.text}
        maxLength={3_000}
        multiline
        ariaLabel="Context text"
        placeholder="Location, deadline, organizer, or another quiet detail"
        onChange={(text) => onChange({ ...section, text })}
      />
      <small>{section.text.length.toLocaleString()} / 3,000</small>
    </div>
  );
}

export function TableEditor({
  section,
  onChange,
}: {
  section: Extract<MessageSection, { type: "table" }>;
  onChange: (section: MessageSection) => void;
}) {
  const columns = section.rows[0]?.cells.length ?? 1;
  const updateCell = (rowIndex: number, columnIndex: number, value: string) =>
    onChange({
      ...section,
      rows: section.rows.map((row, currentRow) =>
        currentRow === rowIndex
          ? {
              ...row,
              cells: row.cells.map((cell, currentColumn) =>
                currentColumn === columnIndex ? { ...cell, text: value } : cell,
              ),
            }
          : row,
      ),
    });
  return (
    <div className="table-editor">
      <div className="table-editor-head">
        <span>
          <Table2 size={15} /> Table
        </span>
        <div>
          <button
            type="button"
            disabled={columns >= 6}
            onClick={() =>
              onChange({
                ...section,
                rows: section.rows.map((row) => ({
                  ...row,
                  cells: [...row.cells, { id: newId(), text: "" }],
                })),
              })
            }
          >
            + Column
          </button>
          <button
            type="button"
            disabled={section.rows.length >= 20}
            onClick={() =>
              onChange({
                ...section,
                rows: [
                  ...section.rows,
                  { id: newId(), cells: Array.from({ length: columns }, () => ({ id: newId(), text: "" })) },
                ],
              })
            }
          >
            + Row
          </button>
        </div>
      </div>
      <div className="table-grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(120px, 1fr))` }}>
        {section.rows.flatMap((row, rowIndex) =>
          row.cells.map((cell, columnIndex) => (
            <SlackField
              mode="rich"
              key={cell.id}
              className={rowIndex === 0 ? "table-heading-cell" : ""}
              value={cell.text}
              maxLength={1_000}
              ariaLabel={`Row ${rowIndex + 1}, column ${columnIndex + 1}`}
              placeholder={rowIndex === 0 ? `Column ${columnIndex + 1}` : "—"}
              onChange={(text) => updateCell(rowIndex, columnIndex, text)}
            />
          )),
        )}
      </div>
      <div className="table-editor-foot">
        <span>
          {section.rows.length} rows · {columns} columns
        </span>
        <div>
          <button
            type="button"
            disabled={columns <= 1}
            onClick={() =>
              onChange({ ...section, rows: section.rows.map((row) => ({ ...row, cells: row.cells.slice(0, -1) })) })
            }
          >
            Remove column
          </button>
          <button
            type="button"
            disabled={section.rows.length <= 1}
            onClick={() => onChange({ ...section, rows: section.rows.slice(0, -1) })}
          >
            Remove row
          </button>
        </div>
      </div>
    </div>
  );
}

export function VoteEditor({
  section,
  onChange,
}: {
  section: Extract<MessageSection, { type: "vote" }>;
  onChange: (section: MessageSection) => void;
}) {
  const updateOption = (index: number, patch: Partial<(typeof section.options)[number]>) =>
    onChange({
      ...section,
      options: section.options.map((option, current) => (current === index ? { ...option, ...patch } : option)),
    });
  const resultHidden = !section.showVoters && !section.showCounts && !section.showPercentages && !section.showBars;
  return (
    <div className="vote-editor">
      <div className="vote-editor-heading">
        <span>
          <VoteIcon size={16} /> Vote
        </span>
        <small>
          {section.selectionMode === "single"
            ? "One choice per person"
            : `Up to ${section.maxSelections} choices per person`}{" "}
          · results update in Slack
        </small>
      </div>
      <div className="vote-question-field">
        <span>Question</span>
        <SlackField
          mode="mrkdwn"
          value={section.question}
          maxLength={3_000}
          onChange={(question) => onChange({ ...section, question })}
          ariaLabel="Vote question"
          placeholder="What came first: the egg or the chicken?"
        />
      </div>

      <fieldset className="vote-selection-settings">
        <legend>How people vote</legend>
        <label>
          <input
            type="radio"
            name={`${section.id}-selection-mode`}
            checked={section.selectionMode === "single"}
            onChange={() => onChange({ ...section, selectionMode: "single", maxSelections: 1 })}
          />
          <span>
            <strong>Single choice</strong>
            <small>Each person selects one option</small>
          </span>
        </label>
        <label>
          <input
            type="radio"
            name={`${section.id}-selection-mode`}
            checked={section.selectionMode === "multiple"}
            onChange={() =>
              onChange({ ...section, selectionMode: "multiple", maxSelections: Math.min(2, section.options.length) })
            }
          />
          <span>
            <strong>Multiple choice</strong>
            <small>People can select more than one option</small>
          </span>
        </label>
        {section.selectionMode === "multiple" && (
          <label className="vote-selection-limit">
            <span>Maximum choices per person</span>
            <select
              data-format-mode="none"
              value={section.maxSelections}
              onChange={(event) => onChange({ ...section, maxSelections: Number(event.target.value) })}
            >
              {section.options.slice(0, -1).map((_, index) => {
                const amount = index + 2;
                return (
                  <option value={amount} key={amount}>
                    {amount}
                  </option>
                );
              })}
            </select>
          </label>
        )}
      </fieldset>
      <div className="vote-options">
        {section.options.map((option, index) => (
          <div className="vote-option-editor" key={option.id}>
            <span className="vote-option-number">{index + 1}</span>
            <div>
              <SlackField
                mode="plain"
                value={option.label}
                maxLength={75}
                onChange={(label) => updateOption(index, { label })}
                ariaLabel={`Vote option ${index + 1}`}
                placeholder="Option"
              />
              <SlackField
                mode="mrkdwn"
                value={option.description ?? ""}
                maxLength={300}
                onChange={(description) => updateOption(index, { description: description || undefined })}
                ariaLabel={`Description for vote option ${index + 1}`}
                placeholder="Short detail (optional)"
              />
            </div>
            <IconButton
              label="Remove vote option"
              disabled={section.options.length <= 2}
              onClick={() => {
                const options = section.options.filter((_, current) => current !== index);
                onChange({ ...section, options, maxSelections: Math.min(section.maxSelections, options.length) });
              }}
            >
              <X size={13} />
            </IconButton>
          </div>
        ))}
        {section.options.length < 10 && (
          <button
            type="button"
            className="add-vote-option"
            onClick={() =>
              onChange({
                ...section,
                options: [
                  ...section.options,
                  { id: `vote_${newId().slice(0, 8)}`, label: `Option ${section.options.length + 1}` },
                ],
              })
            }
          >
            <Plus size={13} /> Add option
          </button>
        )}
      </div>

      <div className="vote-display-settings">
        <div>
          <strong>Live result display</strong>
          <small>{resultHidden ? "Results are fully hidden" : "Choose exactly what appears below the question"}</small>
        </div>
        <div className="vote-display-toggles">
          {(
            [
              ["showVoters", "Voter names"],
              ["showCounts", "Numbers"],
              ["showPercentages", "Percentages"],
              ["showBars", "Bars"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={section[key]}
                onChange={(event) => onChange({ ...section, [key]: event.target.checked })}
              />
              {label}
            </label>
          ))}
        </div>
        <div className="vote-secondary-toggles">
          <label>
            <input
              type="checkbox"
              checked={section.showZeroVoteOptions}
              onChange={(event) => onChange({ ...section, showZeroVoteOptions: event.target.checked })}
            />
            Show choices with zero votes in results
          </label>
          <label>
            <input
              type="checkbox"
              checked={section.allowChange}
              onChange={(event) => onChange({ ...section, allowChange: event.target.checked })}
            />
            Let people change their vote
          </label>
        </div>
      </div>

      <TemplateTextarea
        id={`${section.id}-vote-confirmation`}
        label="Private confirmation"
        help="Shown only to the voter. Use {{vote_option}} for their choice."
        value={section.confirmationText}
        placeholder="Your vote was recorded."
        extraVariables={[["Vote choice", "{{vote_option}}"]]}
        onChange={(confirmationText) => onChange({ ...section, confirmationText })}
      />
    </div>
  );
}

export function MediaEditor({
  section,
  onChange,
}: {
  section: Extract<MessageSection, { type: "image" | "video" | "file" }>;
  onChange: (section: MessageSection) => void;
}) {
  return (
    <div className="media-editor">
      <div className="media-thumb">
        {section.type === "image" ? (
          <img src={section.url} alt="" />
        ) : section.type === "video" ? (
          <Video size={24} />
        ) : (
          <FileText size={24} />
        )}
      </div>
      <div className="media-info">
        <strong>{section.name}</strong>
        <span>
          {section.mimeType} ·{" "}
          {section.size < 1024 * 1024
            ? `${Math.ceil(section.size / 1024)} KB`
            : `${(section.size / 1024 / 1024).toFixed(1)} MB`}
        </span>
      </div>
      {section.type === "image" && (
        <input
          data-format-mode="none"
          className="alt-input"
          value={section.alt}
          onChange={(event) => onChange({ ...section, alt: event.target.value })}
          placeholder="Add alt text"
          aria-label="Image alt text"
        />
      )}
    </div>
  );
}

export function MarkdownEditor({
  section,
  onChange,
}: {
  section: Extract<MessageSection, { type: "markdown" }>;
  onChange: (section: MessageSection) => void;
}) {
  return (
    <div className="markdown-editor">
      <div className="section-label">
        <Braces size={15} /> Advanced markdown <span>tables · tasks · headers · syntax</span>
      </div>
      <textarea
        data-format-mode="markdown"
        value={section.text}
        maxLength={12_000}
        spellCheck={false}
        onChange={(event) => onChange({ ...section, text: event.target.value })}
        placeholder={
          "## Details\n\n- [ ] Bring a laptop\n- [x] Invite sent\n\n| Time | Place |\n| --- | --- |\n| 6 PM | HQ |"
        }
      />
      <div className="field-limit">{section.text.length.toLocaleString()} / 12,000 markdown characters</div>
    </div>
  );
}
